import { AxiosInstance } from "axios"
import auth, { IAuthApi } from "./auth"
import apiKeysApi, { IApiKeysApi } from "./apiKeys"
import ideas, { IIdeasApi } from "./ideas"
import otp, { IOTPApi } from "./otp"
import trading, { ITradingAPI } from "./trading"
import bloomberg, { IBloombergAPI } from "./bloomberg"
import sapi, { ISAPIApi } from "./sapi"
import macroApi, { IMacroApi } from "./macroApi"
import fundamentalsApi, { IFundamentalsApi } from "./fundamentalsApi"
import reconciliationApi, { IReconciliationApi } from "./reconciliation"
import warren, { IWarrenApi } from "./warren"
import tagsApi, { ITagsApi } from "./tags"
import viewModelsApi, { IViewModelsApi } from "./viewModels"

export interface IScientApi {
  users: {
    get: () => Promise<IUser[]>
  }
  auth: IAuthApi
  ideas: IIdeasApi
  trading: ITradingAPI
  profile: {
    get: () => Promise<IUser>
  }
  groups: {
    get: () => Promise<
      {
        id: number
        name: string
      }[]
    >
  }
  uploadImage: (formData: FormData) => Promise<{ location: string }>
  otp: IOTPApi
  bloomberg: IBloombergAPI
  sapi: ISAPIApi
  macro: IMacroApi
  fundamentals: IFundamentalsApi
  reconciliation: IReconciliationApi
  warren: IWarrenApi
  tags: ITagsApi
  apiKeys: IApiKeysApi
  viewModels: IViewModelsApi
}

/**
 * Defines OTP Interface for
 * two factor authentication
 */
export interface IOTP {
  otp_enabled: boolean
  verified: boolean
  otp_base32?: string
  otp_auth_url?: string
}

export interface IUser {
  id: number
  username: string
  email: string
  groups: string[]
  last_login: string
  is_superuser: boolean
  otp?: IOTP
  has_sapi_identity: boolean
}

export interface IGroupsAPI {
  next: number | null
  previous: number | null
  count: number
  results: { id: number; name: string }[]
}

export interface IUsersAPI {
  next: number | null
  previous: number | null
  count: number
  results: IUser[]
}

/**
 * API factory
 * Generates an API using the axiosClient instance provided
 * /!\ Do not use api in components, refer to APIProvider
 */
const api = (axiosClient: AxiosInstance, nscAxiosClient: AxiosInstance) => ({
  users: {
    get: async () => {
      const { data } = await axiosClient.get<IUsersAPI>("/users/")

      return data.results
    },
  },
  auth: auth(axiosClient),
  ideas: ideas(axiosClient),
  profile: {
    get: async () => {
      const { data } = await axiosClient.get<IUser>("/profile")
      return data
    },
  },
  groups: {
    get: async () => {
      const { data } = await axiosClient.get<IGroupsAPI>("/groups/")
      return data.results
    },
  },
  uploadImage: async (formData: FormData) => {
    const { data } = await axiosClient.post("/uploader/image/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return data
  },
  trading: trading(axiosClient),
  otp: otp(axiosClient),
  bloomberg: bloomberg(axiosClient),
  sapi: sapi(axiosClient),
  macro: macroApi(axiosClient),
  fundamentals: fundamentalsApi(axiosClient),
  reconciliation: reconciliationApi(axiosClient),
  warren: warren(nscAxiosClient),
  tags: tagsApi(axiosClient),
  apiKeys: apiKeysApi(axiosClient),
  viewModels: viewModelsApi(axiosClient),
})

export default api
